import { initialize as initializeLDClient, type LDContext } from 'launchdarkly-js-client-sdk'
// biome-ignore lint/style/noRestrictedImports: This is meant to be imported here
import { asyncWithLDProvider, type LDClient, useLDClient, useFlags as useLdFlags } from 'launchdarkly-react-client-sdk'
import { env } from './env.ts'

export type FeatureFlag = keyof LDFlagSet

const activeFlagOverrides =
	import.meta.env.MODE === 'dev' || import.meta.env.MODE === 'test' ? await import('./flag-overrides.tsx') : undefined

// Use Proxy instead of spreading so that LaunchDarkly will not record all flags as evaluated
export const useFlags = () => {
	const tabOverrides = activeFlagOverrides?.useFlagOverrides((s) => s.overrides)
	return new Proxy(useLdFlags<LDFlagSet>(), {
		get: (flags, flag: FeatureFlag) => {
			// Read from LaunchDarkly before applying overrides so that overridden flags are still recorded as evaluated
			const value = flags[flag]
			return tabOverrides?.[flag] ?? value
		},
	})
}
export const useLaunchDarklyFlags = () => (useLDClient()?.allFlags() ?? {}) as Partial<LDFlagSet>
export const flagVariation = (ldClient: LDClient, flag: FeatureFlag) => {
	const value = Boolean(ldClient.variation(flag))
	return activeFlagOverrides?.useFlagOverrides.getState().overrides[flag] ?? value
}
export const FlagOverridesMenu = activeFlagOverrides?.FlagOverridesMenu ?? (() => null)

const ldContext: LDContext = { kind: 'user', key: 'anonymous' }

// Connect to LaunchDarkly domains directly primarily.
// If connection to LaunchDarkly domains fails (eg customer network block it),
// then fall back to CloudFront proxy. If connection via proxy fails also, then
// continue anyway and all flags evaluate to undefined/false/off
const initLDClient = async (): Promise<LDClient> => {
	const directClient = initializeLDClient(env.LD_CLIENTSIDE_ID, ldContext, { sendEventsOnlyForVariation: true })
	try {
		await directClient.waitForInitialization(3)
		return directClient
	} catch (error) {
		console.warn('LaunchDarkly direct connection failed, falling back to proxy', error)
		await directClient.close()
	}
	const proxyClient = initializeLDClient(env.LD_CLIENTSIDE_ID, ldContext, {
		sendEventsOnlyForVariation: true,
		baseUrl: env.LD_FALLBACK_PROXY_URL,
		streamUrl: env.LD_FALLBACK_PROXY_URL,
		eventsUrl: env.LD_FALLBACK_PROXY_URL,
	})
	await proxyClient.waitForInitialization(3).catch((error) => {
		console.warn('LaunchDarkly is unreachable both directly and via proxy, all flags default to off', error)
	})
	return proxyClient
}

export const LDProvider = await asyncWithLDProvider({
	clientSideID: env.LD_CLIENTSIDE_ID,
	context: ldContext,
	ldClient: initLDClient(),
	// Even when passing `ldClient` to the provider, it calls `waitForInitialization` again.
	// For already initialized clients it does nothing, but for certain failure cases this caps
	// infinite/long wait
	timeout: 1,
})
